// Drawer constructor — MVP.
//
// A Drawer is a nested object on `Cabinet.drawers[]`. Each one has a
// user-set height (inches) and a Blum-standard slide depth (mm). The
// drawer's interior width is derived from the cabinet width minus the
// carcass side panels and the slide allowance — designers don't set
// it directly.
//
// One drawer evaluates to:
//   • 2 long sides (left / right of the box)
//   • 2 short sides (front / back of the box)
//   • 1 bottom panel
//   • 1 face panel mounted in front of the cabinet
//
// Hardware (2 slides + 1 pull) is allocated by bom.ts via
// `drawerHardwareCounts`.
//
// All consumers of cabinet geometry (3D scene, BOM, cutlist, exporters,
// previews) reach drawers through `evaluateAllDrawers(cabinet)` so the
// drawer contribution layers on top of the class evaluation. No DSL
// changes required.

import type {
  Cabinet,
  CabinetMeshChunk,
  CabinetPart,
  Drawer,
  Id,
} from "@/lib/types";
import type { DrillEntry } from "./index";
import { boxChunk, COLORS } from "./geometry";

// ── Blum standard slide depths (Tandembox / Movento common set) ──

export interface SlideOption {
  /** Full slide length in mm, as published by Blum. */
  depthMm: number;
  /** Inch equivalent rounded to two decimals (for UI display). */
  depthIn: number;
  label: string;
}

export const BLUM_SLIDE_DEPTHS: SlideOption[] = [
  { depthMm: 270, depthIn: 10.63, label: '270mm (≈ 10½")' },
  { depthMm: 300, depthIn: 11.81, label: '300mm (≈ 12")'  },
  { depthMm: 350, depthIn: 13.78, label: '350mm (≈ 14")'  },
  { depthMm: 400, depthIn: 15.75, label: '400mm (≈ 16")'  },
  { depthMm: 450, depthIn: 17.72, label: '450mm (≈ 18")'  },
  { depthMm: 500, depthIn: 19.69, label: '500mm (≈ 20")'  },
  { depthMm: 550, depthIn: 21.65, label: '550mm (≈ 22")'  },
];

export const DEFAULT_SLIDE_DEPTH_MM = 500;

// ── Shop constants ───────────────────────────────────────────────

/** Carcass panel thickness. Same constant as the seed's `T`. */
const CARCASS_T = 0.75;
/** Drawer box side thickness — 5/8" Baltic birch is the shop standard. */
const SIDE_T = 0.625;
/** 1/4" hardboard or ply bottom slides into a groove in the sides. */
const BOTTOM_T = 0.25;
/** Per-side clearance for Blum side-mount slides. Blum nominal is
 *  12.7mm = 0.500"; we round to 1/2". */
const SLIDE_ALLOW = 0.5;
/** Face panel thickness, matches the door material. */
const FACE_T = 0.75;
// (BOTTOM_INSET was reserved for a groove-bottom variant; current MVP
// renders the bottom as a flush panel so we don't need it yet.)
/** Reveal between face panel and cabinet exterior. */
const REVEAL = 1 / 16;
/** Box depth allowance — drawer box stops short of the slide travel
 *  so the hardware doesn't bind. */
const BOX_DEPTH_ALLOW = 0.5;
/** Z offset of the box back from the cabinet's back face (gives room
 *  for slide brackets and cable runs behind). */
const BOX_REAR_Z_OFFSET = 0.5;
/** Back panel thickness — used to push the drawer box away from the
 *  cabinet back. Matches seed's `BACK_T`. */
const BACK_T = 0.25;

// ── Geometry helpers ─────────────────────────────────────────────

/** Evaluated geometry of one drawer — local cabinet frame. */
interface DrawerGeometry {
  /** X span of the drawer box (between carcass sides + slide allowance). */
  boxX0: number;
  boxX1: number;
  /** Y span: drawer.y is the bottom of the face panel. */
  y0: number;
  y1: number;
  /** Z span of the drawer box. */
  boxBackZ: number;
  boxFrontZ: number;
  /** Face panel X span — full cabinet width less the reveal. */
  faceX0: number;
  faceX1: number;
  /** Face panel Z (sits in front of the cabinet like a door). */
  faceBackZ: number;
  faceFrontZ: number;
  /** Cut dims for parts (post-allowance). */
  boxOuterW: number;
  boxInnerW: number;
  boxDepth: number;
  drawerH: number;
  faceW: number;
}

function geometry(cabinet: Cabinet, drawer: Drawer): DrawerGeometry {
  const W = cabinet.width;
  const D = cabinet.depth;
  const slideDepthIn = drawer.slideDepthMm / 25.4;
  const boxDepth = Math.max(4, slideDepthIn - BOX_DEPTH_ALLOW);

  const boxX0 = CARCASS_T + SLIDE_ALLOW;
  const boxX1 = W - CARCASS_T - SLIDE_ALLOW;
  const boxOuterW = boxX1 - boxX0;
  const boxInnerW = boxOuterW - 2 * SIDE_T;

  const boxBackZ = BACK_T + BOX_REAR_Z_OFFSET;
  const boxFrontZ = boxBackZ + boxDepth;

  return {
    boxX0, boxX1,
    y0: drawer.y, y1: drawer.y + drawer.height,
    boxBackZ, boxFrontZ,
    faceX0: REVEAL, faceX1: W - REVEAL,
    faceBackZ: D, faceFrontZ: D + FACE_T,
    boxOuterW, boxInnerW, boxDepth,
    drawerH: drawer.height,
    faceW: W - 2 * REVEAL,
  };
}

// ── Public: per-drawer evaluator ─────────────────────────────────

export interface DrawerEvalResult {
  meshes: CabinetMeshChunk[];
  parts: CabinetPart[];
}

const FACE_FILL = COLORS.door;
const BOX_FILL  = COLORS.carcass;
const BOTTOM_FILL = COLORS.back;

export function evaluateDrawer(cabinet: Cabinet, drawer: Drawer): DrawerEvalResult {
  const g = geometry(cabinet, drawer);
  const prefix = `drawer_${drawer._id}`;
  const meshes: CabinetMeshChunk[] = [];
  const parts: CabinetPart[] = [];

  // ── Box left side ──
  meshes.push(boxChunk(
    `${prefix}_side_left`,
    [g.boxX0,           g.y0,             g.boxBackZ ],
    [g.boxX0 + SIDE_T,  g.y1,             g.boxFrontZ],
    BOX_FILL,
  ));
  parts.push(cutPart(`${prefix}_side_left`, "Drawer left side", g.boxDepth, g.drawerH, SIDE_T, drawer._id));

  // ── Box right side ──
  meshes.push(boxChunk(
    `${prefix}_side_right`,
    [g.boxX1 - SIDE_T,  g.y0,             g.boxBackZ ],
    [g.boxX1,           g.y1,             g.boxFrontZ],
    BOX_FILL,
  ));
  parts.push(cutPart(`${prefix}_side_right`, "Drawer right side", g.boxDepth, g.drawerH, SIDE_T, drawer._id));

  // ── Box back (short panel between long sides, at boxBackZ) ──
  meshes.push(boxChunk(
    `${prefix}_box_back`,
    [g.boxX0 + SIDE_T,  g.y0,             g.boxBackZ          ],
    [g.boxX1 - SIDE_T,  g.y1,             g.boxBackZ + SIDE_T ],
    BOX_FILL,
  ));
  parts.push(cutPart(`${prefix}_box_back`, "Drawer box back", g.boxInnerW, g.drawerH, SIDE_T, drawer._id));

  // ── Box front (short panel, hidden behind face) ──
  meshes.push(boxChunk(
    `${prefix}_box_front`,
    [g.boxX0 + SIDE_T,  g.y0,             g.boxFrontZ - SIDE_T],
    [g.boxX1 - SIDE_T,  g.y1,             g.boxFrontZ         ],
    BOX_FILL,
  ));
  parts.push(cutPart(`${prefix}_box_front`, "Drawer box front", g.boxInnerW, g.drawerH, SIDE_T, drawer._id));

  // ── Bottom (slides into a groove in the sides — for MVP, render
  //    it as a flat panel inset by BOTTOM_INSET from each long side). ──
  meshes.push(boxChunk(
    `${prefix}_bottom`,
    [g.boxX0,           g.y0,                       g.boxBackZ ],
    [g.boxX1,           g.y0 + BOTTOM_T,            g.boxFrontZ],
    BOTTOM_FILL,
  ));
  parts.push(cutPart(`${prefix}_bottom`, "Drawer bottom", g.boxOuterW, g.boxDepth, BOTTOM_T, drawer._id));

  // ── Face panel (in front of the cabinet, full opening width). ──
  meshes.push(boxChunk(
    `${prefix}_face`,
    [g.faceX0,          g.y0,             g.faceBackZ ],
    [g.faceX1,          g.y1,             g.faceFrontZ],
    FACE_FILL,
  ));
  parts.push(cutPart(`${prefix}_face`, "Drawer face", g.faceW, g.drawerH, FACE_T, drawer._id, "drawer_face"));

  return { meshes, parts };
}

function cutPart(
  id: string, name: string,
  w: number, h: number, thickness: number,
  drawerId: Id,
  /** Optional role marker so BOM can distinguish the face panel. */
  faceMarker?: "drawer_face",
): CabinetPart {
  return {
    id,
    name: `[#${drawerId}] ${name}`,
    w, h, thickness,
    materialId: 0,            // resolved by bom.ts
    edges: { t: null, b: null, l: null, r: null },
    grain: "length",
    qty: 1,
    role: faceMarker === "drawer_face" ? "door" : "custom",
  };
}

// ── Public: roll-up across cabinet ──────────────────────────────

export function evaluateAllDrawers(cabinet: Cabinet): DrawerEvalResult {
  const out: DrawerEvalResult = { meshes: [], parts: [] };
  for (const d of cabinet.drawers ?? []) {
    const one = evaluateDrawer(cabinet, d);
    out.meshes.push(...one.meshes);
    out.parts.push(...one.parts);
  }
  return out;
}

/** Hardware allocation per drawer (called by cabinetBom). For now a
 *  fixed shop standard: two slides + one pull per drawer. */
export interface DrawerHardware {
  slides: number;
  pulls: number;
}

export function drawerHardwareCounts(cabinet: Cabinet): DrawerHardware {
  const n = cabinet.drawers?.length ?? 0;
  return { slides: 2 * n, pulls: n };
}

// ── Slide-rail drillings on carcass side panels ──────────────────
//
// Drawers are NOT part of the cabinet class, so the DSL-side drilling
// templates can't see them. We emit the slide-mount screw pattern here
// (per-cabinet, per-drawer) and merge into `effectiveDrillings()` so it
// flows to MPR, validation, and the PartEditor preview.
//
// Pattern: 4 screws per slide on the 32mm grid (Blum side-mount). All
// four sit in one vertical column near the front of the side panel —
// this is the front mounting cluster of a Tandembox / Movento. Slide
// bottom-front sits SLIDE_LIFT above the drawer's Y position.

/** Carcass side-panel cut-Y origin (cut Y=0) measured up from the
 *  cabinet's local Y=0. Floor cabinets sit on a toe kick; their side
 *  panels start this far up. Wall cabinets have no toe kick. Matches
 *  the convention in `suggestDrawerY` and the default `TOE_KICK_H` in
 *  BaseCabinet / TallCabinet. Per-class param overrides aren't honoured
 *  here yet — designers tweaking TOE_KICK_H will see drawer-slide
 *  drillings shift; revisit if that becomes a real workflow. */
const TOE_KICK_H = 4;

/** Vertical lift of the slide's bottom-front mount point above the
 *  drawer's `y`. Blum specifies ~3mm; we round to 1/8". */
const SLIDE_LIFT = 1 / 8;
/** Distance from the front edge of the side panel to the slide-screw
 *  column. Matches the 37mm front offset used by the shelf-pin row, so
 *  slide screws share an X with the front pin column. */
const SLIDE_FRONT_OFF = 37 / 25.4;
/** Pilot hole — 5mm slide-system screws into 3/4" carcass. Depth is
 *  shy of the panel thickness to avoid blowout. */
const SLIDE_SCREW_DIAM = 5 / 25.4;
const SLIDE_SCREW_DEPTH = 10 / 25.4;
/** Y offsets of the 4 slide-mount screws above the slide's bottom-
 *  front point, on the 32mm grid. */
const SLIDE_SCREW_Y_OFFSETS_MM = [0, 32, 64, 96];

/** Drilling entries for the cabinet-side half of each drawer's slides,
 *  keyed by the side-panel part id (`left_side` / `right_side`). The
 *  caller (effectiveDrillings) appends these onto the side panels'
 *  standard pattern. */
export function drawerSlideDrillings(cabinet: Cabinet): Record<string, DrillEntry[]> {
  const drawers = cabinet.drawers ?? [];
  if (drawers.length === 0) return {};

  const sideOriginY = cabinet.anchor === "wall" || cabinet.anchor === "ceiling" ? 0 : TOE_KICK_H;
  const sideCutW = cabinet.depth;                // matches `cut.w: "D"` on side panels
  const xCol = sideCutW - SLIDE_FRONT_OFF;
  const yOffsetsIn = SLIDE_SCREW_Y_OFFSETS_MM.map((mm) => mm / 25.4);

  const perPanel: DrillEntry[] = [];
  for (const d of drawers) {
    const slideBottomCutY = d.y + SLIDE_LIFT - sideOriginY;
    for (const dy of yOffsetsIn) {
      perPanel.push({
        x: xCol,
        y: slideBottomCutY + dy,
        diameter: SLIDE_SCREW_DIAM,
        depth: SLIDE_SCREW_DEPTH,
        face: "inside",
        kind: "screw",
      });
    }
  }

  return {
    left_side:  perPanel,
    right_side: perPanel,
  };
}

// ── Drawer-specific helpers for the constructor UI ──────────────

/** Reasonable default Y for the next drawer added to a cabinet — stack
 *  on top of existing drawers, leaving a small gap. Caller validates
 *  bounds against the cabinet's available height. */
export function suggestDrawerY(cabinet: Cabinet, height: number): number {
  const existing = cabinet.drawers ?? [];
  if (existing.length === 0) {
    // Start at toe-kick clearance for floor cabinets, at 0 for wall.
    return cabinet.anchor === "floor" ? 4 : 0;
  }
  const top = Math.max(...existing.map((d) => d.y + d.height));
  void height;
  return top + 1 / 16;     // 1/16" reveal between drawers
}
